import { MenuItem, Stack } from '@mui/material';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Sector } from '@/domain/impact/impact.entity';
import {
  Category,
  ImpactCo2Answer,
  TransportationModeValues,
  ValuesTeamMobility,
} from '@/domain/impact-co2/entities/question.entity';
import { selectImpactCo2Answers } from '@/domain/impact-co2/impact-co2.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import {
  AddNewQuiz,
  NewQuizEdition,
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/components/quiz-panel/co2/components/QuizComponents';
import TagForm from '@/ui/pages/app/components/quiz-panel/co2/components/TagForm.tsx';
import { isNumber } from '@/ui/utils/form-validations';

import { ImpactQuestionContext } from '../../../ImpactQuestionContext';
import { useImpactCo2AnswerSubmitDisabled } from '../../hooks/useImpactCo2AnswerSubmitDisabled';

const CATEGORY: Category = 'TEAM_MOBILITY';

export function TeamMobilityForm({
  sector,
  onSubmit,
  onDelete,
  onEdit,
  isExternal = false,
}: {
  sector: Sector;
  onSubmit(answer: ImpactCo2Answer): void;
  onDelete(answer: ImpactCo2Answer): void;
  onEdit(answer: ImpactCo2Answer): void;
  isExternal?: boolean;
}) {
  const { hideCompleted, panelOpen, answersSelectorOverride } = useContext(
    ImpactQuestionContext,
  );
  const [displayNew, setDisplayNew] = useState(false);
  const [displayTag, setDisplayTag] = useState(false);
  const [editing, setEditing] = useState<Array<number>>([]);

  const defaultAnswersSelector = selectImpactCo2Answers<ValuesTeamMobility>({
    sector: sector,
    category: CATEGORY,
    isExternal: isExternal,
  });
  let answersSelector = defaultAnswersSelector;
  if (answersSelectorOverride) {
    answersSelector = answersSelectorOverride;
  }
  const answers: ImpactCo2Answer<ValuesTeamMobility>[] =
    useAppSelector(answersSelector);

  const { disableSubmitActions } = useImpactCo2AnswerSubmitDisabled();

  const handleSubmit = (values: ValuesTeamMobility) => {
    const answer: ImpactCo2Answer = {
      id: -1,
      userId: '',
      sector: sector,
      category: CATEGORY,
      values: values,
      isExternal: isExternal,
      source: '',
    };
    onSubmit(answer);
    setDisplayNew(false);
  };

  const handleEdit = (values: ValuesTeamMobility, id: number) => {
    const answer: ImpactCo2Answer = {
      id,
      userId: '',
      sector,
      category: CATEGORY,
      values,
      isExternal: isExternal,
      source: '',
    };
    onEdit(answer);
    removeFromEditing(answer.id);
  };

  const handleDelete = () => {
    setDisplayNew(false);
  };

  const addToEditing = (id: number) => {
    setEditing([...editing, id]);
  };

  const removeFromEditing = (id: number) => {
    setEditing([...editing].filter((i) => i !== id));
  };

  return (
    <>
      {/* check if we have to hide question if already completed */}
      {!(hideCompleted && answers.length > 0) && (
        <>
          {/* already created answers */}
          {answers
            .sort((a, b) => a.id - b.id)
            .map((answer, i) => (
              <div key={`team-mobility` + i}>
                {editing.includes(answer.id) && !!panelOpen ? (
                  <QuizEdition
                    answer={answer}
                    removeFromEditing={removeFromEditing}
                    index={i}
                    setDisplayTag={() => setDisplayTag(!displayTag)}
                    title={translate(
                      'ImpactQuestion.TEAM_MOBILITY.question-title',
                    )}
                    form={
                      <Form
                        onSubmit={(values) => handleEdit(values, answer.id)}
                        defaultValues={answer.values}
                        onDelete={() => onDelete(answer)}
                        answersCount={answers.length}
                        disableSubmit={disableSubmitActions}
                        disableDelete={disableSubmitActions}
                        isExternal={isExternal}
                        displayTag={displayTag}
                      />
                    }
                  />
                ) : (
                  <QuizNotEdition
                    addToEditing={addToEditing}
                    answeredValues={[
                      translate(
                        `ImpactQuestion.TEAM_MOBILITY.transportationModeValues.${answer.values.transportationMode}`,
                      ),
                      `${translate(
                        `ImpactQuestion.TEAM_MOBILITY.passengerCountTitle`,
                      )}: ${answer.values.passengerCount}`,
                      `${translate(
                        `ImpactQuestion.TEAM_MOBILITY.travelDistanceTitle`,
                      )}: ${answer.values.travelDistance}`,
                      `${translate(
                        `ImpactQuestion.TEAM_MOBILITY.numberOfDaysTitle`,
                      )}: ${answer.values.numberOfDays}`,
                    ]}
                    title={translate(
                      'ImpactQuestion.TEAM_MOBILITY.question-title',
                    )}
                    index={i}
                    answer={answer}
                    quizLabel={answer.values.label}
                    score={answer.values.score}
                  />
                )}
              </div>
            ))}

          {/* new answer */}
          {displayNew || answers.length === 0 ? (
            <NewQuizEdition
              answersLength={answers.length}
              title={translate('ImpactQuestion.TEAM_MOBILITY.question-title')}
              setDisplayTag={() => setDisplayTag(!displayTag)}
              form={
                <Form
                  onSubmit={handleSubmit}
                  onDelete={() => handleDelete()}
                  answersCount={answers.length}
                  disableSubmit={disableSubmitActions}
                  disableDelete={disableSubmitActions}
                  isExternal={isExternal}
                  displayTag={displayTag}
                />
              }
            />
          ) : (
            <AddNewQuiz
              setDisplayNew={setDisplayNew}
              buttonTitle={translate(
                'ImpactQuestion.TEAM_MOBILITY.add-button-title',
              )}
            />
          )}
        </>
      )}
    </>
  );
}

function Form({
  onSubmit,
  onDelete,
  defaultValues,
  answersCount,
  disableSubmit,
  disableDelete,
  isExternal,
  displayTag,
}: {
  onSubmit: (values: ValuesTeamMobility) => void;
  onDelete: () => void;
  defaultValues?: ValuesTeamMobility;
  answersCount: number;
  disableSubmit: boolean;
  disableDelete: boolean;
  displayTag: boolean;
  isExternal?: boolean;
}) {
  const initialValues: ValuesTeamMobility = defaultValues || {
    transportationMode: '2_WHEEL_MOTORIZED_VEHICLE',
    numberOfDays: 0,
    travelDistance: 0,
    passengerCount: 0,
    label: '',
    score: 0,
    tags: [],
  };

  const { handleSubmit, register, formState, setValue } =
    useForm<ValuesTeamMobility>({
      defaultValues: initialValues,
    });

  const { errors } = formState;

  const handleFormSubmit = (data: ValuesTeamMobility) => {
    const values = {
      ...data,
      numberOfDays: parseInt(data.numberOfDays.toString()),
      travelDistance: parseInt(data.travelDistance.toString()),
      passengerCount: parseInt(data.passengerCount.toString()),
    };
    onSubmit(values);
  };

  return (
    <Stack>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack>
          {!isExternal && (displayTag || initialValues.tags.length > 0) && (
            <TagForm
              handleChange={(tags) => setValue('tags', tags)}
              defaultValue={initialValues.tags}
            />
          )}
          <FSTextField
            type="text"
            defaultValue={initialValues.label}
            register={{
              ...register('label'),
            }}
            error={!!errors.label}
            helperText={errors.label?.message}
            placeholder={translate('ImpactQuestion.add-title')}
            placeholderColor="#A29BAE"
            variant="standard"
            sx={{ mb: 2 }}
          />
          <FSTextField
            select
            defaultValue={initialValues.transportationMode}
            label={translate(
              'ImpactQuestion.TEAM_MOBILITY.transportationModeTitle',
            )}
            register={{
              ...register('transportationMode'),
            }}
            error={!!errors.transportationMode}
            helperText={errors.transportationMode?.message}
          >
            {TransportationModeValues.map((value) => (
              <MenuItem key={value} value={value}>
                {translate(
                  `ImpactQuestion.TEAM_MOBILITY.transportationModeValues.${value}`,
                )}
              </MenuItem>
            ))}
          </FSTextField>

          <FSTextField
            type="number"
            label={translate(
              'ImpactQuestion.TEAM_MOBILITY.passengerCountTitle',
            )}
            defaultValue={initialValues.passengerCount}
            register={{
              ...register('passengerCount', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.passengerCount}
            helperText={errors.passengerCount?.message}
          />

          <FSTextField
            type="number"
            label={translate(
              'ImpactQuestion.TEAM_MOBILITY.travelDistanceTitle',
            )}
            defaultValue={initialValues.travelDistance}
            register={{
              ...register('travelDistance', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.travelDistance}
            helperText={errors.travelDistance?.message}
            step={0.01}
          />

          <FSTextField
            type="number"
            label={translate('ImpactQuestion.TEAM_MOBILITY.numberOfDaysTitle')}
            defaultValue={initialValues.numberOfDays}
            register={{
              ...register('numberOfDays', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.numberOfDays}
            helperText={errors.numberOfDays?.message}
          />

          <Stack direction={'row'} marginTop={1} gap={1}>
            <FSPrimaryButton type="submit" disabled={disableSubmit}>
              {translate(defaultValues ? 'Form.save' : 'Form.add')}
            </FSPrimaryButton>
            {onDelete && answersCount > 0 && (
              <FSSecondaryButton onClick={onDelete} disabled={disableDelete}>
                {translate('Form.delete')}
              </FSSecondaryButton>
            )}
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
